'use client';

import { Slider as SliderPrimitive } from 'radix-ui';
import * as React from 'react';

import { tv } from '../../lib/tv';

/**
 * The Figma Slider pieces (Slider & Stepper ↳ Slider, 92:403), keyed on the
 * attributes Radix sets (`data-disabled`) plus `data-dragging` on the thumb
 * being dragged.
 *
 * Track: 4px bg/emphasis, fully rounded; fill brand/strong (Deep Olive,
 * ≥ 3:1 on white), border/strong when disabled.
 * Thumb: an 18px bg/surface circle with a 2px brand/strong inside stroke and
 * Shadow/SM. Hover and Dragging swap the shadow for a 5px olive/200 halo,
 * Focus for Focus/Ring; Disabled is a border/strong stroke, no shadow.
 *
 * The focusable `role="slider"` element is 0×0 and the circle hangs off it
 * centred: Radix measures the thumb to keep it inside the track ("in bounds"),
 * which would shift it off the value. Figma centres the thumb on the value
 * (at 60% the circle is centred at 168 of 280). A 32px ::after is the hit area.
 */
export const sliderStyles = tv({
  slots: {
    root: 'group/slider relative flex h-6 w-full touch-none items-center select-none data-disabled:cursor-not-allowed',
    track: 'bg-bg-emphasis relative h-1 w-full grow overflow-hidden rounded-full',
    range:
      'bg-brand-strong group-data-disabled/slider:bg-border-strong absolute h-full rounded-full',
    thumb: [
      'group/thumb relative block size-0 outline-none',
      "after:absolute after:top-0 after:left-0 after:size-8 after:-translate-1/2 after:rounded-full after:content-['']",
      'data-disabled:after:cursor-not-allowed',
    ],
    circle: [
      'border-brand-strong bg-bg-surface absolute top-0 left-0 size-[18px] -translate-1/2 rounded-full border-2 shadow-sm',
      'transition-[box-shadow,border-color] duration-[120ms] ease-out',
      'group-hover/thumb:shadow-[0_0_0_5px_var(--color-brand-subtle-hover)]',
      'group-data-dragging/thumb:shadow-[0_0_0_5px_var(--color-brand-subtle-hover)]',
      'group-focus-visible/thumb:shadow-focus-ring',
      'group-data-disabled/thumb:border-border-strong group-data-disabled/thumb:shadow-none!',
    ],
  },
});

type ThumbProps = React.ComponentProps<typeof SliderPrimitive.Thumb>;

export type SliderPrimitiveProps = React.ComponentProps<typeof SliderPrimitive.Root> & {
  /** Extra props for each thumb (`role="slider"`), by index. */
  getThumbProps?: (index: number) => ThumbProps;
  /** Content rendered inside each thumb (e.g. the value tooltip), by index. */
  renderThumbContent?: (index: number) => React.ReactNode;
};

/** shadcn Slider (Radix), restyled to the Figma Slider. One thumb per value. */
function Slider({
  className,
  defaultValue,
  value,
  min = 0,
  max = 100,
  getThumbProps,
  renderThumbContent,
  ...props
}: SliderPrimitiveProps) {
  const count = (value ?? defaultValue ?? [min]).length;
  const styles = sliderStyles();
  return (
    <SliderPrimitive.Root
      data-slot="slider"
      defaultValue={defaultValue}
      value={value}
      min={min}
      max={max}
      className={styles.root({ className })}
      {...props}
    >
      <SliderPrimitive.Track data-slot="slider-track" className={styles.track()}>
        <SliderPrimitive.Range data-slot="slider-range" className={styles.range()} />
      </SliderPrimitive.Track>
      {Array.from({ length: count }, (_, index) => {
        const { className: thumbClassName, ...thumbProps } = getThumbProps?.(index) ?? {};
        return (
          <SliderPrimitive.Thumb
            data-slot="slider-thumb"
            data-index={index}
            data-last={index === count - 1 || undefined}
            key={index}
            className={styles.thumb({ className: thumbClassName })}
            {...thumbProps}
          >
            <span
              data-slot="slider-thumb-circle"
              aria-hidden
              className={styles.circle()}
            />
            {renderThumbContent?.(index)}
          </SliderPrimitive.Thumb>
        );
      })}
    </SliderPrimitive.Root>
  );
}

export { Slider };
